import { useAuth } from '@auth/useAuth'
import { getTenantMetadata, LanguagesEnum, TenantMetadata } from '@kudos/http-client'
import { useQuery } from '@tanstack/react-query'

export const TENANT_METADATA_QK = 'TENANT_METADATA_QUERY_KEY'
// This data will show by default and in the event of failure to fetch tenant info
const defaultMetadata: TenantMetadata = {
  language: LanguagesEnum.ENGLISH,
  name: 'Kudos',
  logoUrls: null,
  bannerUrls: null,
  trialDaysRemaining: null,
  tenantId: null,
}

const getCurrentMetadata = (): Promise<TenantMetadata> => {
  return getTenantMetadata(window.location.hostname)
}

/**
 * This hook makes information about the current tenant context available for
 * UI, branding purposes. This data is cached because it is slow moving and mostly
 * used for display and therefore doesn't matter if it lags behind server. This data
 * should *NOT* be used to infer authentication state or for any kind of functionality.
 *
 * `tenantId` is part of the cache key so the metadata refetches when the session
 * transitions from unauthenticated/user-scope to tenant-scope on the same host. On
 * the lobby domain (`my.…`) password login keeps the user on the same hostname, so
 * without this the branding fetched while unauthenticated (defaults) would stay
 * cached until it goes stale or the page is refreshed. SSO avoids this because it
 * redirects to the tenant's domain with a full page load.
 */
export const useTenantMetadata = (): [TenantMetadata, boolean] => {
  const { tenantId } = useAuth()
  const { data: metadata, isSuccess } = useQuery<TenantMetadata>({
    queryKey: [TENANT_METADATA_QK, window.location.hostname, tenantId],
    queryFn: getCurrentMetadata,
    staleTime: 15 * 60 * 1000,
  })
  return [metadata || defaultMetadata, isSuccess]
}
